import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { productApi } from '../services/api';

const CartContext = createContext();

const API_URL =
  process.env.REACT_APP_API_URL ||
  'https://swadvedamasalasbe.onrender.com/api';

export const CartProvider = ({ children }) => {
  const { token, user } = useAuth();

  const [cart, setCart] = useState([]);
  const [initialized, setInitialized] = useState(false); // ✅ NEW

  // =========================
  // FETCH CART FROM DB
  // =========================
  const fetchCart = async () => {
    if (!token) return;

    try {
      const res = await axios.get(`${API_URL}/cart`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const items = res.data.cart || [];

     const detailedCart = await Promise.all(
  items.map(async (item) => {
    if (!item.productId) {
      console.error("❌ Missing productId:", item);
      return null;
    }

    try {
      const productRes = await productApi.getById(item.productId);

      return {
        _id: item.productId,
        name: productRes.data.name,
        price: Number(productRes.data.price || 0),
        image: productRes.data.image,
        quantity: item.quantity
      };
    } catch (err) {
      console.error("❌ Product not found:", item.productId);
      return null; // ✅ prevent crash + stop spam
    }
  })
);

setCart(detailedCart.filter(Boolean));
      setCart(detailedCart.filter(Boolean));

    } catch (error) {
      console.error('❌ Fetch cart error:', error);
    }
  };

  // =========================
  // RESET INIT FLAG ON USER CHANGE
  // =========================
  useEffect(() => {
    setInitialized(false);
  }, [user]);

  // =========================
  // LOAD CART (CONTROLLED)
  // =========================
  useEffect(() => {
    const loadCart = async () => {
      if (user && token && !initialized) {
        await fetchCart();        // ✅ only once
        setInitialized(true);     // ✅ prevent override
      } else if (!user) {
        setCart([]);
      }
    };

    loadCart();
  }, [user, token, initialized]);

  // =========================
  // ADD TO CART
  // =========================
  const addToCart = async (product, qty = 1) => {
    if (!token) {
      throw new Error("NOT_AUTHENTICATED");
    }

    try {
      await axios.post(
        `${API_URL}/cart`,
        {
          productId: product._id,
          quantity: qty,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      await fetchCart(); // ✅ always refresh full data

    } catch (error) {
      console.error('❌ Add to cart error:', error);
    }
  };

  // =========================
  // REMOVE ITEM
  // =========================
  const removeFromCart = async (productId) => {
    try {
      await axios.delete(`${API_URL}/cart/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await fetchCart(); // ✅ refresh

    } catch (error) {
      console.error('❌ Remove error:', error);
    }
  };

  // =========================
  // UPDATE QUANTITY
  // =========================
  const updateQuantity = async (productId, qty) => {
    try {
      await axios.put(
        `${API_URL}/cart/${productId}`,
        { quantity: qty },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      await fetchCart(); // ✅ refresh

    } catch (error) {
      console.error('❌ Update error:', error);
    }
  };

  // =========================
  // CLEAR CART
  // =========================
  const clearCart = async () => {
    try {
      await axios.delete(`${API_URL}/cart`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setCart([]);
    } catch (error) {
      console.error('❌ Clear error:', error);
    }
  };

  // =========================
  // TOTAL PRICE
  // =========================
  const getTotalPrice = () => {
    return cart.reduce((total, item) => {
      return total + (item.price || 0) * item.quantity;
    }, 0);
  };

  // =========================
  // TOTAL ITEMS
  // =========================
  const getTotalItems = () => {
    if (!user) return 0;
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        setCart,
        fetchCart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotalPrice,
        getTotalItems,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};